import { useLocation, useNavigate } from "react-router-dom";
import { Check, ChevronDown, Heart, Plus } from "lucide-react";
import { useWedding } from "@/hooks/useWedding";
import { daysUntil, formatDate } from "@/lib/utils";
import { WEDDING_STAGE_LABELS } from "@/types/wedding";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";

/**
 * The one deliberately "wedding" flourish in the chrome: a place-card
 * styled switcher that always shows the countdown to the active wedding,
 * the way a planner keeps a date circled on a desk calendar.
 */
export function WeddingSwitcher() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { weddings, activeWedding, setActiveWeddingId } = useWedding();
  const { language, locale, t } = useLanguage();

  if (!activeWedding) {
    return (
      <span className="text-sm text-muted-foreground">{t("No weddings yet")}</span>
    );
  }

  const remaining = daysUntil(activeWedding.weddingDate);
  const countdownLabel =
    Number.isNaN(remaining) ? t("Date pending") : remaining > 0 ? language === "es" ? `${remaining} d restantes` : `${remaining}d to go` : remaining === 0 ? t("Today") : t("Completed");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="group flex items-center gap-3 rounded-lg border border-border bg-surface py-1.5 pl-3 pr-2.5 shadow-xs transition-colors hover:bg-muted">
          <Heart className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
          <span className="flex flex-col items-start leading-tight">
            <span className="font-display text-sm font-semibold">{activeWedding.coupleNames}</span>
            <span className="text-[11px] text-muted-foreground">
              {formatDate(activeWedding.weddingDate, locale)}
            </span>
          </span>
          <Badge variant={remaining >= 0 && remaining <= 30 ? "warning" : "secondary"} className="ml-1">
            {countdownLabel}
          </Badge>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        <DropdownMenuLabel>{t("Weddings in this organization")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {weddings.map((wedding) => (
          <DropdownMenuItem
            key={wedding.id}
            onSelect={() => {
              const section = pathname.match(/^\/weddings\/[^/]+(\/.*)?$/)?.[1] ?? "";
              setActiveWeddingId(wedding.id);
              navigate(`/weddings/${encodeURIComponent(wedding.id)}${section}`);
            }}
            className="flex-col items-start gap-0.5 py-2"
          >
            <span className="flex w-full items-center gap-2">
              <span className="flex-1 truncate font-medium">{wedding.coupleNames}</span>
              {wedding.id === activeWedding.id && <Check className="h-4 w-4 shrink-0 text-primary" />}
            </span>
            <span className="text-xs text-muted-foreground">
              {formatDate(wedding.weddingDate, locale)} · {t(WEDDING_STAGE_LABELS[wedding.stage])}
            </span>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem disabled>
          <Plus className="h-4 w-4" />
          <span>{t("New wedding")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
